import * as styles from "@/client/core/components/access-gate.css";
import type { OpenExternalLink } from "@/client/core/Core.api";
import { button } from "@/client/ui/design-system/components/button.css";
import { createEl } from "@/client/utils/createEl";

const kSoundcheckUrl = "https://splice.com/soundcheck";

/**
 * Shown on the home page when a logged-in user resolves to no audience tier.
 * Purely informational — server enforcement stays login-only; audience
 * gating is a UX layer. Soundcheck is the tier anyone can ask to join, so the
 * page points there rather than ending at "no".
 */
export const createAccessGate = (openEmbeddedLink?: OpenExternalLink): HTMLElement => {
    const title = createEl("h1", styles.title, "Backstage");
    const message = createEl(
        "p",
        styles.message,
        "Backstage access isn't enabled for your account yet. Join Soundcheck to get early access to new tools from Splice R&D.",
    );
    // A link, so the browser opens the new window on the website and in the
    // sidepanel. In the Labs plugin, whose webview can discard that, the
    // click goes to the host instead (see createEmbeddedLinkOpener).
    const cta = createEl(
        "a",
        `${button({ variant: "primary", size: "medium", theme: "dark" })} ${styles.cta}`,
        "Join Soundcheck",
        (el) => {
            el.href = kSoundcheckUrl;
            el.target = "_blank";
            // No `noreferrer`, so Soundcheck can see signups came from here.
            el.rel = "noopener";
            if (openEmbeddedLink !== undefined) {
                el.addEventListener("click", (event) => {
                    event.preventDefault();
                    openEmbeddedLink(kSoundcheckUrl);
                });
            }
        },
    );
    const card = createEl("div", styles.card, [title, message, cta]);
    return createEl("div", styles.container, [card]);
};
